import { uiSurface } from "shared/ui/uiSurface"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  ReportEntry,
  ReportParameters,
  UniversalReport,
} from "../../apis/appApi"

const DELIVERED_EVENTS = ["CUSTOMER_DELIVERY", "PROVIDER_DELIVERY", "PICKUP"]
const CANCELLED_EVENTS = ["CANCELLATION"]

type TabProps = {
  entries: ReportEntry[]
  report: UniversalReport
  parameters: ReportParameters
}

type SummaryLine = {
  paymentType: string
  numberOfOrders: number
  requestedAmount: number
  collectedAmount: number
}

const buildSummary = (entries: ReportEntry[]): SummaryLine[] => {
  const map = new Map<string, SummaryLine>()
  for (const entry of entries) {
    const pt = entry.order.paymentType || "empty"
    const existing = map.get(pt) || {
      paymentType: pt,
      numberOfOrders: 0,
      requestedAmount: 0,
      collectedAmount: 0,
    }
    existing.numberOfOrders++
    existing.requestedAmount += entry.order.amount || 0
    existing.collectedAmount += entry.order.collectedAmount || 0
    map.set(pt, existing)
  }
  return Array.from(map.values()).sort((a, b) =>
    a.paymentType.localeCompare(b.paymentType),
  )
}

const SummaryTable: FC<{
  lines: SummaryLine[]
  t: (key: string) => string
}> = ({ lines, t }) => {
  const totals = lines.reduce(
    (acc, l) => ({
      numberOfOrders: acc.numberOfOrders + l.numberOfOrders,
      requestedAmount: acc.requestedAmount + l.requestedAmount,
      collectedAmount: acc.collectedAmount + l.collectedAmount,
    }),
    { numberOfOrders: 0, requestedAmount: 0, collectedAmount: 0 },
  )

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("reports.fields.paymentType")}</TableCell>
            <TableCell align="right">
              {t("reports.fields.numberOfOrders")}
            </TableCell>
            <TableCell align="right">
              {t("reports.fields.requestedAmount")}
            </TableCell>
            <TableCell align="right">
              {t("reports.fields.collectedAmount")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.paymentType}>
              <TableCell>
                {t(`reports.paymentType.${line.paymentType}`)}
              </TableCell>
              <TableCell align="right">{line.numberOfOrders}</TableCell>
              <TableCell align="right">
                {line.requestedAmount.toFixed(2)}
              </TableCell>
              <TableCell align="right">
                {line.collectedAmount.toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ "& td": { fontWeight: "bold" } }}>
            <TableCell>{t("reports.fields.total")}</TableCell>
            <TableCell align="right">{totals.numberOfOrders}</TableCell>
            <TableCell align="right">
              {totals.requestedAmount.toFixed(2)}
            </TableCell>
            <TableCell align="right">
              {totals.collectedAmount.toFixed(2)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export const AccountingTab: FC<TabProps> = ({
  entries,
  report,
  parameters,
}) => {
  const { t } = useTranslation()

  const deliveredEntries = useMemo(
    () =>
      entries.filter(
        (e) =>
          e.traceability && DELIVERED_EVENTS.includes(e.traceability.event),
      ),
    [entries],
  )

  const cancelledEntries = useMemo(
    () =>
      entries.filter(
        (e) =>
          e.traceability && CANCELLED_EVENTS.includes(e.traceability.event),
      ),
    [entries],
  )

  const globalSummary = useMemo(
    () => buildSummary(deliveredEntries),
    [deliveredEntries],
  )

  const courierSummaries = useMemo(() => {
    const map = new Map<
      number,
      { courier: ReportEntry["courier"]; entries: ReportEntry[] }
    >()
    for (const entry of deliveredEntries) {
      if (!entry.courier) continue
      const existing = map.get(entry.courier.id) || {
        courier: entry.courier,
        entries: [],
      }
      existing.entries.push(entry)
      map.set(entry.courier.id, existing)
    }
    return Array.from(map.values()).sort((a, b) =>
      `${a.courier!.lastName} ${a.courier!.firstName}`.localeCompare(
        `${b.courier!.lastName} ${b.courier!.firstName}`,
      ),
    )
  }, [deliveredEntries])

  const orderColumns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: "reference",
        headerName: t("reports.fields.reference"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "customer",
        headerName: t("reports.fields.customer"),
        flex: 2,
        minWidth: 200,
        valueGetter: (_value: any, row: any) => {
          const c = row.client
          const a = row.address
          const name = c ? `${c.firstName} ${c.lastName}` : ""
          const addr = a ? `${a.streetNumber} ${a.streetName}, ${a.city}` : ""
          return `${name} - ${addr}`
        },
      },
      {
        field: "notes",
        headerName: t("reports.fields.orderNotes"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "preparedBy",
        headerName: t("reports.fields.preparedBy"),
        width: 130,
        valueGetter: (_value: any, row: any) => row.creationOperatorCode || "",
      },
      {
        field: "deliveryMessage",
        headerName: t("reports.fields.deliveryMessage"),
        flex: 1,
        minWidth: 150,
        valueGetter: (_value: any, row: any) => row.traceabilityMessage || "",
      },
      {
        field: "courier",
        headerName: t("reports.fields.courier"),
        width: 150,
        valueGetter: (_value: any, row: any) => row.courierName || "",
      },
      {
        field: "amount",
        headerName: t("reports.fields.requestedAmount"),
        width: 120,
        align: "right",
        headerAlign: "right",
        valueGetter: (_value: any, row: any) =>
          row.amount?.toFixed(2) ?? "0.00",
      },
      {
        field: "collectedAmount",
        headerName: t("reports.fields.collectedAmount"),
        width: 120,
        align: "right",
        headerAlign: "right",
        valueGetter: (_value: any, row: any) =>
          row.collectedAmount?.toFixed(2) ?? "0.00",
      },
      {
        field: "paymentType",
        headerName: t("reports.fields.paymentType"),
        width: 140,
        valueGetter: (_value: any, row: any) =>
          t(`reports.paymentType.${row.paymentType || "empty"}`),
      },
    ]

    if (parameters.appConfig.orderExternalReferenceEnabled) {
      cols.splice(2, 0, {
        field: "externalReference",
        headerName: t("reports.fields.externalReference"),
        width: 150,
      })
    }

    if (parameters.appConfig.multipleCollectingAccountsEnabled) {
      cols.push({
        field: "collectingAccount",
        headerName: t("reports.fields.collectingAccount"),
        width: 150,
      })
    }

    if (report.providerSize > 1) {
      cols.push({
        field: "providerName",
        headerName: t("reports.fields.provider"),
        width: 150,
      })
    }

    return cols
  }, [t, parameters, report.providerSize])

  const toGridRows = (items: ReportEntry[]) =>
    items.map((e) => ({
      id: e.order.id,
      reference: e.order.reference,
      client: e.order.client,
      address: e.order.address,
      notes: e.order.notes,
      creationOperatorCode: e.creationTraceability?.operatorCode || "",
      traceabilityMessage: e.traceability?.message || "",
      courierName: e.courier
        ? `${e.courier.lastName} ${e.courier.firstName}`
        : "",
      amount: e.order.amount,
      collectedAmount: e.order.collectedAmount,
      paymentType: e.order.paymentType,
      externalReference: e.order.externalReference,
      collectingAccount: e.order.collectingAccount,
      providerName: e.order.providerName,
    }))

  return (
    <Box
      {...uiSurface("UI-DLV2-192")}
      data-testid="accounting-tab"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {/* Global Summary */}
      <Accordion data-testid="accounting-global-summary" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("reports.accounting.globalSummary")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {globalSummary.length > 0 ? (
            <SummaryTable lines={globalSummary} t={t} />
          ) : (
            <Typography color="text.secondary">
              {t("reports.empty.orders")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Courier Summaries */}
      <Accordion data-testid="accounting-couriers-summary" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("reports.accounting.couriersSummary")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {courierSummaries.length > 0 ? (
            courierSummaries.map(({ courier, entries: cEntries }) => (
              <Accordion key={courier!.id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {courier!.lastName} {courier!.firstName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SummaryTable lines={buildSummary(cEntries)} t={t} />
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography color="text.secondary">
              {t("reports.empty.couriers")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Delivered Orders */}
      <Accordion data-testid="accounting-delivered" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("reports.accounting.delivered")} ({deliveredEntries.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid
            data-testid="accounting-delivered-grid"
            rows={toGridRows(deliveredEntries)}
            columns={orderColumns}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            autoHeight
            disableRowSelectionOnClick
            density="compact"
          />
        </AccordionDetails>
      </Accordion>

      {/* Cancelled Orders */}
      <Accordion data-testid="accounting-cancelled">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("reports.accounting.cancelled")} ({cancelledEntries.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {cancelledEntries.length > 0 ? (
            <DataGrid
              data-testid="accounting-cancelled-grid"
              rows={toGridRows(cancelledEntries)}
              columns={orderColumns}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              autoHeight
              disableRowSelectionOnClick
              density="compact"
            />
          ) : (
            <Typography color="text.secondary">
              {t("reports.empty.cancelled")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Tags Summary */}
      <Accordion data-testid="accounting-tags-summary">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("reports.accounting.tagsSummary")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {report.tags.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("reports.fields.tag")}</TableCell>
                    <TableCell align="right">
                      {t("reports.fields.count")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.tags.map((tc) => (
                    <TableRow key={tc.tag.id}>
                      <TableCell>{tc.tag.label}</TableCell>
                      <TableCell align="right">{tc.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">
              {t("reports.empty.tags")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
